import {FormField} from '../../../types/form-builder.types';
import {Star} from 'lucide-react';
import {useState, useMemo, useRef} from 'react';

interface RatingMatrixFieldProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
}

export function RatingMatrixField({ field, value, onChange }: RatingMatrixFieldProps) {
  const config = field.config;
  const rows = Array.isArray(config.rows) ? config.rows : [];
  const maxRating = config.ratingMax || 5;
  const matrixValue = value || {};
  const [hoverState, setHoverState] = useState<Record<string, number>>({});

  // Store randomization seed in ref to prevent re-randomization
  const randomSeedRef = useRef<number | null>(null);

  // Randomize rows ONCE on mount if configured
  const displayRows = useMemo(() => {
    if (!config.randomizeRows || rows.length === 0) {
      return rows;
    }

    // Generate seed once and store it
    if (randomSeedRef.current === null) {
      randomSeedRef.current = Math.random();
    }

    // Create a copy and shuffle using Fisher-Yates algorithm
    const shuffled = [...rows];
    // Use the stored seed for consistent randomization
    let seed = randomSeedRef.current;
    const random = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }, [rows, config.randomizeRows, randomSeedRef.current]); // Stable dependencies

  const handleRatingChange = (rowValue: string, rating: number) => {
    onChange({ ...matrixValue, [rowValue]: rating });
  };

  return (
    <div className="space-y-0">
      {displayRows.map((row, rowIndex) => {
        const currentValue = matrixValue[row.value] || 0;
        const hoverValue = hoverState[row.value];
        const isAlternate = config.alternateRowColors && rowIndex % 2 === 1;
        const isFirstRow = rowIndex === 0;
        const isLastRow = rowIndex === displayRows.length - 1;

        return (
          <div
            key={row.id}
            className="flex items-center justify-between gap-4 px-0 py-3 transition-colors duration-150"
            style={{
              borderBottom: isLastRow
                ? '1px solid var(--ef-border, #E2E8F0)'
                : '1px solid color-mix(in oklab, var(--ef-border, #E2E8F0) 60%, transparent)',
              borderTop: isFirstRow ? '1px solid var(--ef-border, #E2E8F0)' : 'none',
              background: isAlternate
                ? 'var(--ef-surface, #FAFBFC)'
                : 'transparent',
              paddingLeft: isAlternate || isFirstRow || isLastRow ? '0' : '0',
              paddingRight: isAlternate || isFirstRow || isLastRow ? '0' : '0'
            }}
          >
            {/* Row Label */}
            <span
              className="text-sm font-normal flex-1 min-w-[160px]"
              style={{ color: 'var(--ef-text, #1E293B)' }}
            >
              {row.label}
            </span>

            {/* Rating Stars */}
            <div className="flex gap-1">
              {Array.from({ length: maxRating }, (_, index) => {
                const ratingValue = index + 1;
                const isActive = hoverValue 
                  ? ratingValue <= hoverValue 
                  : ratingValue <= currentValue;

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleRatingChange(row.value, ratingValue)}
                    onMouseEnter={() => setHoverState({ ...hoverState, [row.value]: ratingValue })}
                    onMouseLeave={() => setHoverState({ ...hoverState, [row.value]: 0 })}
                    className="transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-1 focus:ring-offset-1 rounded p-0.5"
                    style={{
                      '--tw-ring-color': 'var(--ef-primary, #3B82F6)',
                    } as React.CSSProperties}
                    aria-label={`Rate ${ratingValue} out of ${maxRating}`}
                  >
                    <Star
                      className="w-5 h-5 transition-all duration-200"
                      style={{
                        fill: isActive ? 'var(--ef-accent, #F59E0B)' : 'transparent',
                        color: isActive ? 'var(--ef-accent, #F59E0B)' : 'var(--ef-border, #D1D5DB)',
                      }}
                      strokeWidth={1.5}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
